import React from 'react';

import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import { faTableRows } from '@rtkwlf/pro-solid-svg-icons/faTableRows';
import { useQuery } from '@tanstack/react-query';

import {
  useGetOrganizationOptions,
  useGetOrganizationSpidrBlueprintsOptions,
} from '@apis/track-public/query-options';

import { Empty } from '../../../../components/empty/empty';
import { useBlueprintIDSearchParam } from '../../../../hooks/use-blueprint-id-search-param';
import { useSecurityJourneyTranslations } from '../../../../locales/use-security-journey-translations';
import { DEFAULT_ORG_TIER } from '../../_constants/default-org-tier';
import { BlueprintDetailsContent } from '../blueprint-details-content/blueprint-details-content';
import { BlueprintDetailsLoading } from '../blueprint-details-loading';

/* -------------------------------------------------------------------------------------------------
 *  BlueprintDetails
 * -----------------------------------------------------------------------------------------------*/
export const BlueprintDetails: React.FunctionComponent = () => {
  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Get blueprintID search param from url
   */
  const { blueprintID } = useBlueprintIDSearchParam();

  /**
   * Query for fetching all blueprints
   */
  const { data, isLoading } = useQuery(
    useGetOrganizationSpidrBlueprintsOptions(),
  );

  /**
   * Get the current organization tier
   */
  const {
    data: orgTier,
    isLoading: isLoadingOrgTier,
    error: isErrorOrgTier,
  } = useQuery({
    ...useGetOrganizationOptions(),
    select: (organization) => {
      return organization.tierInfo;
    },
    refetchOnMount: false,
    throwOnError: false,
  });

  /**
   * Find a blueprint with blueprintID from all blueprints
   */
  const blueprint = React.useMemo(() => {
    if (data && blueprintID && blueprintID in data.blueprints) {
      return data.blueprints[blueprintID];
    }
  }, [blueprintID, data]);

  if (blueprintID === null) {
    return (
      <Empty
        icon={faTableRows}
        heading={t(
          'spidrLibrary.blueprintDetails.noSpidrSelectedError.heading',
        )}
        message={t(
          'spidrLibrary.blueprintDetails.noSpidrSelectedError.message',
        )}
      />
    );
  }

  if (isLoading || isLoadingOrgTier) {
    return <BlueprintDetailsLoading />;
  }

  if (blueprint === undefined || isErrorOrgTier) {
    return (
      <Empty
        icon={faSearch}
        heading={t('spidrLibrary.blueprintDetails.noSpidrFoundError.heading')}
        message={t('spidrLibrary.blueprintDetails.noSpidrFoundError.message')}
      />
    );
  }

  return (
    <BlueprintDetailsContent
      blueprint={blueprint}
      orgTier={orgTier ?? DEFAULT_ORG_TIER}
    />
  );
};
